import React, {Fragment, useRef, useEffect} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {Col, Overlay, Row} from "react-bootstrap";
import {FormatterFactory} from "../../../Models/Extensions/Core/Formatters";
import {Glyphicon} from "../../Common/GlyphIcon";
import {service as FilterMethodService} from '../../../Services/FilterMethodService';
import {service as GroupingMethodService} from '../../../Services/GroupingMethodService';
import {service as QueryToolService} from '../../../Services/QueryToolService';

import ReactTable, {ReactTableDefaults} from 'react-table';
import withDraggableColumns from 'react-table-hoc-draggable-columns';

import {ReactTableScrollbarEnhance} from "./ReactTableScrollbarEnhance";
import {PaginationOverride} from "./PaginationOverride";
import {NAME_PLACEHOLDER} from './Constants';
import {AddColumnPopover} from "./Popovers";
import {RESET_NEW_COLUMNS,
    SET_ALL_COLUMNS,
    SET_COLUMNS,
    SET_FILTER_METHODS,
    SET_GROUPING_METHODS,
    SET_INSERT_COLUMN_ID,
    SET_PAGE,
    SET_PAGE_SIZE,
} from "../../../Redux/Actions/Types/queryToolActionTypes";
import {useDeleteColumns, useGetPlaceholderGuid, useInsertColumns, useQueryData,} from "../../../Hooks/QueryToolHooks";
import {useUpdateEffect} from "../../../Hooks/EffectHooks";

const ReactTableDraggableColumns = withDraggableColumns(ReactTableScrollbarEnhance(ReactTable));

const NoDataMessage = props => (
    null
);

export const QueryToolTable = props => {
    const dispatch = useDispatch();
    const queryData = useQueryData();
    const getPlaceholderGuid = useGetPlaceholderGuid();
    const insertColumns = useInsertColumns();
    const deleteColumns = useDeleteColumns();

    const targetNode = useRef(null);

    const {
        columns,
        newColumns,
        sorted,
        data,
        count,
        page,
        pageSize,
        isStale,
        showAdd,
    } = useSelector(state => state.queryToolState);

    useEffect(() => {
        loadData();
    }, [])

    useUpdateEffect(() => {
        queryData(false);
    }, [page, pageSize])

    const loadData = () => {
        Promise.all([
            QueryToolService.Get(),
            GroupingMethodService.Get(),
            FilterMethodService.Get(),
        ])
        .then(([columnSources, groupingMethods, filterMethods]) => {
            dispatch({
                type: SET_ALL_COLUMNS,
                allColumns: columnSources.result.filter(x => !x.hidden)
            });
            dispatch({
                type: SET_GROUPING_METHODS,
                groupingMethods: groupingMethods.result
            });
            dispatch({
                type: SET_FILTER_METHODS,
                filterMethods: filterMethods.result
            });
        });
    }

    const renderAddColumnPopover = () => {
        const saveNewColumns = (guid) => {
            if (newColumns[0]?.data?.name === NAME_PLACEHOLDER) {
                deleteColumns([guid]);
                dispatch({type: RESET_NEW_COLUMNS});
            } else {
                dispatch({type: RESET_NEW_COLUMNS});
                queryData();
            }
        }

        const cancelAdd = () => {
            const guids = [...newColumns].map(x => x.data.guid);

            if (guids.length > 0) deleteColumns(guids);
            dispatch({type: RESET_NEW_COLUMNS});
        }

        return (
            <Overlay
                container={targetNode}
                target={targetNode.current}
                onHide={() => saveNewColumns(getPlaceholderGuid)}
                show={showAdd}
                rootClose={true}
            >
                <AddColumnPopover saveNewColumns={saveNewColumns} cancelAdd={cancelAdd} />
            </Overlay>
        );
    }

    const renderCell = (props) => {
        if (props.value === null) {
            return <span className="stale">NULL</span>
        }
        const {formatter, dataType, sbOptions = null, shortName, suffix} = props.column.data;
        let value = props.value;

        if(sbOptions) {
            value = sbOptions?.[value] ?? value;
        }

        switch (dataType) {
            case 'int':
                if(shortName?.endsWith?.('Id')) {
                    // don't put commas in database ids
                    break;
                }
            case 'float':
                const formatterName = formatter ? formatter.name : 'NumberFormatter';
                const formatterParams = formatter ? formatter.params : [];

                if (value != null) {
                    let formattedValue = new FormatterFactory(formatterName, formatterParams).format(value);
                    value = isNaN(value) ? value : formattedValue;
                }
                break;
            case 'bool':
                value = Number(value) ? 'Yes' : 'No';
            default:
                value = value;
        }

        return suffix ? `${value} ${suffix}` : value || null;
    }

    return <Fragment>
        <props.Title />
        <div>
            <hr className="special-hr"/>
        </div>
        <Row>
            <Col xs={12}>
                <div ref={targetNode}>
                    {renderAddColumnPopover()}
                </div>
                <Row key="filters">
                    <props.FilterComponent />
                </Row>
                {
                    columns.length ?
                    <ReactTableDraggableColumns
                        draggableColumns={{
                            mode: 'reorder',
                            draggable: columns.map((x) => x.accessor),
                            enableColumnWideDrag: false,
                            onDropSuccess: (reorder) => {
                                const columnsClone = [...columns];
                                columnsClone.splice(reorder.a, 0, columns.splice(reorder.b, 1)[0]);

                                dispatch({type: SET_COLUMNS, columns: columnsClone});
                            }
                        }}
                        data={data}
                        columns={columns}
                        sorted={sorted}
                        sortable={false} // disable default sorting
                        defaultPageSize={pageSize}
                        pageSize={pageSize}
                        page={page}
                        pages={Math.ceil(count/pageSize)}
                        onPageSizeChange={(pageSize, page) => {
                            dispatch({type: SET_PAGE_SIZE, pageSize});
                            dispatch({type: SET_PAGE, page});
                        }}
                        onPageChange={page => {
                            document.getElementsByClassName('rt-tbody')[0].scrollTop = 0;
                            dispatch({type: SET_PAGE, page});
                        }}
                        minRows={10}
                        className={`-striped -highlight ${isStale ? 'stale' : ''} ${showAdd ? 'sb-query-tool-modal-backdrop' : ''}`}
                        getResizerProps={(state, rowInfo, column, instance) => {
                            return {
                                onMouseOver: (e, handleOriginal) => {
                                    dispatch({type: SET_INSERT_COLUMN_ID, insertColumnId: column.data.guid});
                                },
                                onMouseLeave: (e, handleOriginal) => {
                                    // Give time to hover over insert icon
                                    setTimeout(() => {
                                        dispatch({type: SET_INSERT_COLUMN_ID, insertColumnId: null});
                                    }, 150)
                                }
                            }
                        }}
                        getTrProps={ state => ({ style: { minWidth: 0 } }) }
                        getTheadTrProps={ state => ({ style: { minWidth: 0 } }) }
                        column={{...ReactTableDefaults.column,
                            width: 250,
                            Cell: renderCell,
                        }}
                        NoDataComponent={NoDataMessage}
                        PaginationComponent={PaginationOverride}
                        manual
                    /> :
                    <div className="sb-query-tool-empty-state">
                        <Glyphicon
                            glyph="plus-sign"
                            onClick={() => insertColumns(null, [{name: NAME_PLACEHOLDER}])}
                        />
                        <div className="empty-table">
                            <Glyphicon
                                className="glyphicon-plus-sign"
                                glyph="plus-sign"
                                onClick={() => insertColumns(null, [{name: NAME_PLACEHOLDER}])}
                            />
                            <div className="empty-table-title">
                                Start A Query
                            </div>
                        </div>
                    </div>
                }
            </Col>
        </Row>
    </Fragment>
}


